import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { useJsonLd } from "@/lib/useJsonLd";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import { BRAND } from "@/lib/brand";

const areaSlug = (a) => a.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPosts, setAllPosts] = useState([]);

  useSeo({
    title: post ? `${post.title} | Meridian Bed Bug Guide` : "Meridian Bed Bug Guide",
    description: post?.meta_description || post?.excerpt || "Meridian Bed Bug Guide — practical, specialist answers on bed bugs, treatment and prevention in Vancouver and the Lower Mainland.",
    path: `/guide/${slug}`,
    type: "article",
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPost(null);
    const today = new Date().toISOString().slice(0, 10);
    base44.entities.BlogPost.filter({ slug, status: "published" })
      .then((rows) => {
        if (!active) return;
        const p = rows?.[0] || null;
        setPost(p && (!p.published_date || p.published_date <= today) ? p : null);
        setLoading(false);
      })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    base44.entities.BlogPost.list("-published_date", 120)
      .then((rows) => {
        setAllPosts((rows || [])
          .filter((p) => p.status === "published")
          .filter((p) => !p.published_date || p.published_date <= today));
      })
      .catch(() => {});
  }, []);

  /* page title, meta, canonical and social tags set by useSeo */

  useJsonLd("article-jsonld", post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description || post.excerpt,
    "datePublished": post.published_date,
    "dateModified": post.published_date,
    "author": { "@type": "Organization", "name": "Meridian Bed Bug Treatment" },
    "publisher": {
      "@type": "Organization",
      "name": "Meridian Bed Bug Treatment",
      "logo": { "@type": "ImageObject", "url": "https://meridianbedbug.ca/logo_v2.svg" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://meridianbedbug.ca/guide/${post.slug}` }
  } : {});

  /* BreadcrumbList JSON-LD is now emitted by the <Breadcrumbs /> component below. */

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-32">
        <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
        <div className="mt-6 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-5 text-center">
        <h1 className="font-heading text-3xl font-bold text-primary">Article not found</h1>
        <Button asChild className="mt-6 h-11 bg-accent text-white hover:bg-accent/90"><Link to="/guide">Back to The Bed Bug Guide</Link></Button>
      </div>
    );
  }

  const idx = allPosts.findIndex((p) => p.slug === slug);
  const next = idx >= 0 && allPosts.length > 1 ? allPosts[(idx + 1) % allPosts.length] : null;
  const related = allPosts.filter((p) => p.slug !== slug).slice(0, 6);

  return (
    <article className="mx-auto max-w-3xl px-5 py-32 lg:px-8">
      <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "The Bed Bug Guide", to: "/guide" }, { name: post.title }]} className="mb-6" />
      <Link to="/guide" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back to The Bed Bug Guide
      </Link>
      {post.published_date && (
        <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
          {new Date(post.published_date + "T00:00:00").toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
        </p>
      )}
      <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight tracking-tight text-primary sm:text-5xl">{post.title}</h1>
      <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{post.excerpt}</p>
      <div className="mt-10 h-px w-full bg-border" />
      <div className="mt-8 space-y-6">
        {(post.body || []).map((p, i) => {
          if (typeof p === "string" && p.startsWith("## ")) {
            return <h2 key={i} className="font-heading text-2xl font-bold text-primary mt-10">{p.slice(3)}</h2>;
          }
          return <p key={i} className="text-base leading-[1.8] text-foreground">{p}</p>;
        })}
      </div>
      <div className="mt-14 rounded-lg border border-border bg-muted/30 p-7">
        <p className="font-heading text-xl font-bold text-primary">Ready to treat the problem?</p>
        <p className="mt-2 text-sm text-muted-foreground">Book a specialized bed bug treatment for your property.</p>
        <Button asChild className="mt-5 h-11 bg-accent text-white hover:bg-accent/90"><Link to="/#booking">Book Treatment <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
      </div>
      <div className="mt-8 rounded-lg border border-border bg-muted/20 p-6">
        <p className="font-heading text-sm font-bold uppercase tracking-[0.16em] text-muted-foreground">Treatment in your area</p>
        <p className="mt-2 text-sm text-muted-foreground">See preparation, signs and follow-up for your community.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {BRAND.areas.map((a) => (
            <Link key={a} to={a === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${areaSlug(a)}`} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent/40 hover:text-accent">{a}</Link>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">Keep reading</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.id} to={`/guide/${r.slug}`} className="group flex h-full flex-col rounded-lg border border-border bg-muted/20 p-5 transition-colors hover:border-accent/40 hover:bg-muted/40">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{r.published_date || "Guide"}</span>
                <span className="mt-3 font-heading text-sm font-bold leading-snug text-primary">{r.title}</span>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-accent">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        {next && (
          <Link to={`/guide/${next.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
            Next: {next.title} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
    </article>
  );
}